import React from 'react';
import { DropdownProps } from 'semantic-ui-react';
import { find, groupBy, mapValues, partition, pickBy, sortBy } from 'lodash-es';
import { notNil } from '../../../../utils/typescriptUtils';
import { ProductImageParts } from '../../utils';

// don't put things in the same file since that breaks treeshaking w/ dynamic import
// intentionally moves most of the logic into this file; makes A/B test code easy to remove if needed
export const computeModalPicker = ({
  preferCurveImages,
  preferredModelCode,
  userSelectedMatchingImages,
  shouldSuppressModelChanger,
}: {
  preferCurveImages: boolean;
  preferredModelCode: string | null;
  userSelectedMatchingImages: ProductImageParts[];
  shouldSuppressModelChanger: boolean;
}) => {
  const [digitalMarketingImages, ecomImages] = partition(
    userSelectedMatchingImages,
    (item) => item.backgroundCode === 'b2' || item.backgroundCode === 'b3'
  );
  const imagesWithModelCodes = ecomImages
    .map((item) => (item.modelCode && item.backgroundCode === 'b1' ? { ...item, modelCode: item.modelCode } : null))
    .filter(notNil);

  // ? [REVISIT] logging if `imagesWIthModelCodes` if a different length than `ecomImages`, where (modelOptions > 1) ...we would be hiding invalid images without model codes
  const imagesByModelCode = groupBy(imagesWithModelCodes, 'modelCode');

  let modelOptions = Object.entries(imagesByModelCode)
    .map(([modelCode, ecomImages]) => {
      const modelData = MODELS_DATA[modelCode];
      return modelData ? { ...modelData, image: ecomImages[0]?.url, ecomImages } : undefined;
    })
    .filter(notNil);
  modelOptions = preferCurveImages ? sortBy(modelOptions, (item) => -item.sizeNumber) : modelOptions;

  const preferredModel = modelOptions.find((item) => item.modelCode === preferredModelCode);
  const fallbackModelOption = modelOptions[0];

  if (!shouldSuppressModelChanger && notNil(fallbackModelOption)) {
    const pickerCurrentModel = preferredModel ?? fallbackModelOption;
    const digitalMarketingImagesMatchingThisModel = digitalMarketingImages.filter(
      (img) => img.sizeCode === pickerCurrentModel.ecomImages?.[0]?.sizeCode
    );
    const previewImages = userSelectedMatchingImages.filter(
      (item) =>
        !!find(
          (pickerCurrentModel.ecomImages as ProductImageParts[]).concat(digitalMarketingImagesMatchingThisModel),
          pickBy(item)
        )
    );

    return {
      previewImages,
      shouldRenderModelChanger: true,
      options: modelOptions,
      value: pickerCurrentModel.modelCode,
    };
  }
};
// -- could also fix the JSON file

export const MODELS_DATA = window.server_pdp_data?.metaFields.modelsData
  ? mapValues(
      window.server_pdp_data.metaFields.modelsData as Record<string, IModelJsonData>,
      ({ modelMeasurements, sizeDescription, sizeNumber, sizeLabel }, modelCode) => ({
        modelMeasurements,
        modelCode,
        sizeDescription,
        sizeLabel,
        sizeNumber: Number(sizeNumber),
      })
    )
  : {};

export type ModelData = typeof MODELS_DATA[0];

export interface IModelChanger {
  value: string;
  onChange: (event: React.SyntheticEvent<HTMLElement>, data: DropdownProps) => void;
  modelOptions: (ModelData & { image?: string })[];
  wrapperClass?: string;
}

export interface IModelJsonData {
  modelNumber: string;
  sizeNumber: string;
  sizeLabel: string;
  sizeDescription: string;
  modelMeasurements: string;
}
export interface IModelsJsonData {
  [key: string]: IModelJsonData;
}
